'use client';

import { ProductVariant } from 'shopify-storefront-api-typings';
import { VariantLifecycleStatus } from '../utilities/get-product-lifecycle-status';
import { useBuyContext } from '@app/[lang]/products/context';
import dayjs from 'dayjs';
import { ReviewsResponseBody } from '@app/lib/reviewsio/methods/fetch-product-reviews';
import { Product, WithContext, AggregateRating, Offer } from 'schema-dts';

interface StructuredDataProps {
    title: string;
    images: any;
    shortDescription: string;
    defaultVariant: ProductVariant;
    productHandle: string;
    countryCode: string;
    reviews: ReviewsResponseBody | null;
}

const ProductStructuredData = ({
    title,
    images,
    shortDescription,
    defaultVariant,
    countryCode,
    productHandle,
    reviews,
}: StructuredDataProps) => {
    const { selectedVariant, variantLifecycleStatus } = useBuyContext();
    const variant = selectedVariant || defaultVariant;

    const flatImagesArray: string[] = [];
    for (const image of images.slice(0, 4)) {
        flatImagesArray.push(image.src);
    }


    const structuredData: WithContext<Product> = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: title,
        image: flatImagesArray,
        description: shortDescription,
        sku: variant.sku,
        brand: {
            '@type': 'Brand',
            name: 'MAAP',
        },
    };

    if (reviews) {
        structuredData.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: reviews.average,
            reviewCount: reviews.count,
        } as AggregateRating;
    }

    if (variant.compareAtPriceV2) {
        structuredData.offers = {
            '@type': 'Offer',
            url: getProductURL(countryCode, productHandle),
            priceCurrency: variant.priceV2.currencyCode,
            price: variant.priceV2.amount,
            priceValidUntil: dayjs().add(3, 'day').format('YYYY-MM-DD'),
            itemCondition: 'https://schema.org/NewCondition',
            availability: `https://schema.org/${getSchemaAvailability(
                variantLifecycleStatus
            )}`,
        } as Offer;
    }

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
    );
};

export default ProductStructuredData;

function getSchemaAvailability(lifecycleStatus: VariantLifecycleStatus | null) {
    switch (lifecycleStatus) {
        case VariantLifecycleStatus.ACTIVE:
            return 'InStock';
        case VariantLifecycleStatus.SOLD_OUT:
            return 'OutOfStock';
        // Not sure if coming soon return value is right? Please help me check here: https://schema.org/ItemAvailability
        case VariantLifecycleStatus.COMING_SOON:
            return 'PreOrder';
        case VariantLifecycleStatus.DISCONTINUED:
            return 'Discontinued';
        // Not sure if notify return value is right? Please help me check here: https://schema.org/ItemAvailability
        case VariantLifecycleStatus.NOTIFY:
            return 'PreOrder';
        default:
            return 'InStock';
    }
}

function getProductURL(countryCode: string, handle: string): string {
    switch (countryCode) {
        case 'AU':
            return `https://maap.cc/products/${handle}`;
        case 'US':
            return `https://maap.cc/us/products/${handle}`;
        case 'EU':
            return `https://maap.cc/eu/products/${handle}`;
        case 'UK':
            return `https://maap.cc/uk/products/${handle}`;
        default:
            return `https://maap.cc/products/${handle}`;
    }
}
